const API_BASE_URL = 'https://completehomecarecleaning.com/api';
// Para producción en el mismo dominio puede cambiarse a '/api'

const DEFAULT_ADMIN_TOKEN = 'ffbfa0d262e7802663439360799c496dc09785b86fda1a9e2cd7831b69bd2fe0';
const ADMIN_TOKEN_KEY = 'chcc_admin_token';

function isReaddyDev(): boolean {
  if (typeof window === 'undefined') return false;
  return window.location.hostname.includes('readdy.ai');
}

function getAdminToken(): string {
  if (!isReaddyDev()) return '';
  try {
    const stored = localStorage.getItem(ADMIN_TOKEN_KEY);
    return stored || DEFAULT_ADMIN_TOKEN;
  } catch {
    return DEFAULT_ADMIN_TOKEN;
  }
}

export function setAdminToken(token: string): void {
  try {
    localStorage.setItem(ADMIN_TOKEN_KEY, token);
  } catch {
    // storage not available
  }
}

export function clearAdminToken(): void {
  try {
    localStorage.removeItem(ADMIN_TOKEN_KEY);
  } catch {
    // storage not available
  }
}

export function clearAdminSession(): void {
  try {
    localStorage.removeItem(ADMIN_TOKEN_KEY);
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin');
    localStorage.removeItem('chcc_admin_user');
    localStorage.removeItem('admin_session');
    sessionStorage.clear();
  } catch {
    // storage not available
  }
}

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
}

// Hero Slide (API -> Internal)
export interface HeroSlideApi {
  id: number;
  tag: string;
  title: string;
  subtitle: string;
  image: string;
  cta_primary: string;
  cta_secondary: string;
  sort_order: number;
  active: number | boolean;
}

export interface HeroSlide {
  id: number;
  tag: string;
  title: string;
  subtitle: string;
  image: string;
  ctaPrimary: string;
  ctaSecondary: string;
}

// Service (API -> Internal)
export interface ServiceApi {
  id: number;
  title: string;
  short_description: string;
  full_description: string;
  icon: string;
  image_url?: string;
  sort_order: number;
  active: number | boolean;
}

export interface Service {
  id: number;
  icon: string;
  title: string;
  description: string;
  image?: string;
}

// Service Area (API -> Internal)
export interface ServiceAreaApi {
  id: number;
  city_name: string;
  state: string;
  display_order: number;
  active: number | boolean;
}

export interface ServiceArea {
  id: number;
  name: string;
  state: string;
  order: number;
  active: boolean;
}

// Gallery Image (API -> Internal)
export interface GalleryImageApi {
  id: number;
  image_url: string;
  title: string;
  alt_text: string;
  category: string;
  sort_order: number;
  active: number | boolean;
}

export interface GalleryImage {
  id: number;
  src: string;
  alt: string;
  title: string;
  category: string;
}

// Content Item
export interface ContentItem {
  content_key: string;
  content_value: string;
}

// Facebook Settings
export interface FacebookSettings {
  facebook_url: string;
  embed_code: string;
  show_feed?: number | boolean;
}

// Contact Form
export interface ContactFormPayload {
  full_name: string;
  phone: string;
  email: string;
  service_type: string;
  property_address?: string;
  preferred_date?: string;
  message?: string;
}

function getAdminHeaders(isJson: boolean = true): Record<string, string> {
  const headers: Record<string, string> = {
    Accept: 'application/json',
  };
  if (isJson) {
    headers['Content-Type'] = 'application/json';
  }
  if (isReaddyDev()) {
    headers['X-Admin-Token'] = getAdminToken();
  }
  return headers;
}

function handleUnauthorized(status: number): boolean {
  if (status === 401) {
    // Only redirect to admin login if we're actually on an admin route.
    // Public pages must never be redirected.
    const isAdminRoute =
      typeof window !== 'undefined' &&
      window.location.pathname.startsWith('/admin');
    if (isAdminRoute && typeof window !== 'undefined' && window.REACT_APP_NAVIGATE) {
      window.REACT_APP_NAVIGATE('/admin/login');
    }
    return true;
  }
  return false;
}

async function apiGetPublic<T>(endpoint: string): Promise<ApiResponse<T>> {
  try {
    const res = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: 'GET',
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) {
      if (handleUnauthorized(res.status)) {
        return { success: false, message: 'Unauthorized — Please login again' };
      }
      return { success: false, message: `HTTP ${res.status}` };
    }
    const json = (await res.json()) as ApiResponse<T>;
    return json;
  } catch (err) {
    return {
      success: false,
      message: err instanceof Error ? err.message : 'Network error',
    };
  }
}

async function apiGet<T>(endpoint: string): Promise<ApiResponse<T>> {
  try {
    const res = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: 'GET',
      headers: getAdminHeaders(false),
      credentials: 'include',
    });
    if (!res.ok) {
      if (handleUnauthorized(res.status)) {
        return { success: false, message: 'Unauthorized — Please login again' };
      }
      return { success: false, message: `HTTP ${res.status}` };
    }
    const json = (await res.json()) as ApiResponse<T>;
    return json;
  } catch (err) {
    return {
      success: false,
      message: err instanceof Error ? err.message : 'Network error',
    };
  }
}

async function apiPost<T>(endpoint: string, body: unknown): Promise<ApiResponse<T>> {
  try {
    const res = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: 'POST',
      headers: getAdminHeaders(),
      credentials: 'include',
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      if (handleUnauthorized(res.status)) {
        return { success: false, message: 'Unauthorized — Please login again' };
      }
      return { success: false, message: `HTTP ${res.status}` };
    }
    const json = (await res.json()) as ApiResponse<T>;
    return json;
  } catch (err) {
    return {
      success: false,
      message: err instanceof Error ? err.message : 'Network error',
    };
  }
}

async function apiUpload<T>(endpoint: string, formData: FormData): Promise<ApiResponse<T>> {
  try {
    const headers: Record<string, string> = {
      Accept: 'application/json',
    };
    if (isReaddyDev()) {
      headers['X-Admin-Token'] = getAdminToken();
    }
    const res = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: formData,
    });
    if (!res.ok) {
      if (handleUnauthorized(res.status)) {
        return { success: false, message: 'Unauthorized — Please login again' };
      }
      return { success: false, message: `HTTP ${res.status}` };
    }
    const json = (await res.json()) as ApiResponse<T>;
    return json;
  } catch (err) {
    return {
      success: false,
      message: err instanceof Error ? err.message : 'Network error',
    };
  }
}

// ===== Admin Auth =====
export interface AdminUser {
  id: number;
  email: string;
  name: string;
  role: string;
}

export interface AdminLoginPayload {
  email: string;
  password: string;
}

export async function loginAdmin(
  payload: AdminLoginPayload,
): Promise<ApiResponse<AdminUser>> {
  try {
    const res = await fetch('https://completehomecarecleaning.com/api/admin/login.php', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      return { success: false, message: `HTTP ${res.status}` };
    }
    const json = (await res.json()) as ApiResponse<AdminUser & { api_token?: string }>;
    if (json.success && json.data?.api_token && isReaddyDev()) {
      setAdminToken(json.data.api_token);
    }
    return json;
  } catch (err) {
    return {
      success: false,
      message: err instanceof Error ? err.message : 'Network error',
    };
  }
}

export async function getAdminMe(): Promise<AdminUser | null> {
  try {
    const res = await fetch('https://completehomecarecleaning.com/api/admin/me.php', {
      method: 'GET',
      headers: getAdminHeaders(false),
      credentials: 'include',
    });
    if (!res.ok) {
      if (handleUnauthorized(res.status)) {
        return null;
      }
      return null;
    }
    const json = (await res.json()) as ApiResponse<AdminUser>;
    if (!json.success || !json.data) return null;
    return json.data;
  } catch {
    return null;
  }
}

export async function logoutAdmin(): Promise<ApiResponse<{ message?: string }>> {
  try {
    const res = await fetch('https://completehomecarecleaning.com/api/admin/logout.php', {
      method: 'POST',
      headers: getAdminHeaders(),
      credentials: 'include',
      body: JSON.stringify({}),
    });
    if (!res.ok) {
      if (handleUnauthorized(res.status)) {
        return { success: false, message: 'Unauthorized — Please login again' };
      }
      return { success: false, message: `HTTP ${res.status}` };
    }
    const json = (await res.json()) as ApiResponse<{ message?: string }>;
    return json;
  } catch (err) {
    return {
      success: false,
      message: err instanceof Error ? err.message : 'Network error',
    };
  } finally {
    clearAdminSession();
  }
}

// ===== Hero =====
export async function fetchHeroSlides(): Promise<HeroSlide[]> {
  const res = await apiGetPublic<HeroSlideApi[] | { slides: HeroSlideApi[] }>('/hero/list.php');
  if (!res.success || !res.data) return [];
  // Handle both array-direct and wrapped { slides: [...] } formats
  const raw: HeroSlideApi[] = Array.isArray(res.data)
    ? (res.data as HeroSlideApi[])
    : ((res.data as { slides?: HeroSlideApi[] }).slides ?? []);
  if (raw.length === 0) return [];
  return raw
    .filter((s) => s.active === 1 || s.active === true)
    .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
    .map((s) => ({
      id: s.id,
      tag: s.tag,
      title: s.title,
      subtitle: s.subtitle,
      image: s.image,
      ctaPrimary: s.cta_primary,
      ctaSecondary: s.cta_secondary,
    }));
}

// ===== Services =====
export async function fetchServices(): Promise<Service[]> {
  const res = await apiGetPublic<ServiceApi[] | { services: ServiceApi[] }>('/services/list.php');
  if (!res.success || !res.data) return [];
  // Handle both array-direct and wrapped { services: [...] } formats
  const raw: ServiceApi[] = Array.isArray(res.data)
    ? (res.data as ServiceApi[])
    : ((res.data as { services?: ServiceApi[] }).services ?? []);
  if (raw.length === 0) return [];
  return raw
    .filter((s) => s.active === 1 || s.active === true)
    .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
    .map((s) => ({
      id: s.id,
      icon: s.icon,
      title: s.title,
      description: s.short_description || s.full_description || '',
      image: s.image_url,
    }));
}

// ===== Service Areas =====
export async function fetchServiceAreas(): Promise<ServiceArea[]> {
  const res = await apiGetPublic<ServiceAreaApi[] | { areas: ServiceAreaApi[] }>('/service-areas/list.php');
  if (!res.success || !res.data) return [];
  // Handle both array-direct and wrapped { areas: [...] } formats
  const raw: ServiceAreaApi[] = Array.isArray(res.data)
    ? (res.data as ServiceAreaApi[])
    : ((res.data as { areas?: ServiceAreaApi[] }).areas ?? []);
  if (raw.length === 0) return [];
  return raw
    .filter((a) => a.active === 1 || a.active === true)
    .sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0))
    .map((a) => ({
      id: a.id,
      name: a.city_name,
      state: a.state,
      order: a.display_order ?? 0,
      active: true,
    }));
}

// ===== Gallery =====
export async function fetchGallery(): Promise<GalleryImage[]> {
  const res = await apiGetPublic<GalleryImageApi[] | { images: GalleryImageApi[] }>('/gallery/list.php');
  if (!res.success || !res.data) return [];
  // Handle both array-direct and wrapped { images: [...] } formats
  const raw: GalleryImageApi[] = Array.isArray(res.data)
    ? (res.data as GalleryImageApi[])
    : ((res.data as { images?: GalleryImageApi[] }).images ?? []);
  if (raw.length === 0) return [];
  return raw
    .filter((g) => g.active === 1 || g.active === true)
    .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
    .map((g) => ({
      id: g.id,
      src: g.image_url,
      alt: g.alt_text || g.title || '',
      title: g.title || '',
      category: g.category || '',
    }));
}

// ===== Content =====
export interface ContentMap {
  [key: string]: string;
}

export async function fetchContent(lang: string): Promise<ContentMap> {
  const res = await apiGetPublic<{ items: ContentItem[] }>(`/content/get.php?lang=${encodeURIComponent(lang)}`);
  if (!res.success || !res.data || !Array.isArray(res.data.items)) {
    return {};
  }
  const map: ContentMap = {};
  for (const item of res.data.items) {
    if (item.content_key) {
      map[item.content_key] = item.content_value || '';
    }
  }
  return map;
}

// ===== Facebook =====
export async function fetchFacebookSettings(): Promise<FacebookSettings | null> {
  const res = await apiGetPublic<{ settings: FacebookSettings }>('/facebook/get.php');
  if (!res.success || !res.data || !res.data.settings) return null;
  return {
    facebook_url: res.data.settings.facebook_url || '',
    embed_code: res.data.settings.embed_code || '',
    show_feed: res.data.settings.show_feed ?? 1,
  };
}

// ===== Contact =====
export async function submitContact(
  payload: ContactFormPayload,
): Promise<ApiResponse<{ id?: number }>> {
  return apiPost<{ id?: number }>('/contact/create.php', payload);
}

// ===== Quote =====
export interface QuoteFormPayload {
  full_name: string;
  phone: string;
  email: string;
  property_address?: string;
  property_type: string;
  bedrooms?: string;
  bathrooms?: string;
  kitchens?: string;
  living_rooms?: string;
  dining_rooms?: string;
  offices?: string;
  garage?: string;
  square_feet?: string;
  cleaning_frequency?: string;
  cleaning_type?: string;
  extras?: string[];
  preferred_date?: string;
  preferred_time?: string;
  budget_range?: string;
  notes?: string;
}

export async function submitQuote(
  payload: QuoteFormPayload,
): Promise<ApiResponse<{ id?: number }>> {
  return apiPost<{ id?: number }>('/quotes/create.php', payload);
}

// ===== AI Lead =====
export interface AiLeadPayload {
  full_name: string;
  phone: string;
  email?: string;
  appointment_type: string;
  service_type: string;
  property_address?: string;
  preferred_date?: string;
  preferred_time?: string;
  notes?: string;
}

export async function submitAiLead(
  payload: AiLeadPayload,
): Promise<ApiResponse<{ id?: number }>> {
  return apiPost<{ id?: number }>('/ai-leads/create.php', payload);
}

// ===== Robust array extractor =====
// Handles ALL known response shapes — does NOT require success:true
// Shapes: array direct, { data:[] }, { items:[] }, { records:[] }, { messages:[] },
// { quotes:[] }, { appointments:[] }, { leads:[] }, { results:[] }, { list:[] },
// { data:{ items:[] } }, { data:{ records:[] } }, { success:true, data:[] } etc.
function extractArray<T>(res: ApiResponse<unknown>): T[] {
  // Cast to a plain object so we can probe every key regardless of success flag
  const raw = res as unknown as Record<string, unknown>;
  const d = res.data;

  // 1. data is already an array (covers { success:true, data:[...] })
  if (Array.isArray(d)) return d as T[];

  // 2. data is an object — look for known array keys inside it
  if (d && typeof d === 'object') {
    const obj = d as Record<string, unknown>;
    for (const key of ['data', 'items', 'records', 'results', 'list', 'messages', 'quotes', 'appointments', 'leads']) {
      if (Array.isArray(obj[key])) return obj[key] as T[];
    }
  }

  // 3. array lives at the top-level of the raw response object
  for (const key of ['data', 'items', 'records', 'results', 'list', 'messages', 'quotes', 'appointments', 'leads']) {
    if (Array.isArray(raw[key])) return raw[key] as T[];
  }

  // 4. entire raw response IS an array (backend returned [] directly)
  if (Array.isArray(raw)) return raw as unknown as T[];

  return [];
}

// Low-level fetch that returns the raw parsed JSON without any ApiResponse wrapper assumption.
// Used by the 4 Messages tabs to bypass the success-gate in extractArray.
async function apiGetRaw(endpoint: string): Promise<Record<string, unknown>> {
  try {
    const res = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: 'GET',
      headers: getAdminHeaders(false),
      credentials: 'include',
    });
    if (!res.ok) {
      handleUnauthorized(res.status);
      return { success: false, _httpStatus: res.status };
    }
    const json = await res.json();
    return json as Record<string, unknown>;
  } catch (err) {
    return { success: false, _error: err instanceof Error ? err.message : 'Network error' };
  }
}

// Extract array from a raw JSON object using the same multi-key probe strategy.
function extractArrayRaw<T>(raw: Record<string, unknown>): T[] {
  // The raw object itself might be an array
  if (Array.isArray(raw)) return raw as unknown as T[];

  // Check inside .data first
  const d = raw['data'];
  if (Array.isArray(d)) return d as T[];
  if (d && typeof d === 'object') {
    const inner = d as Record<string, unknown>;
    for (const key of ['data', 'items', 'records', 'results', 'list', 'messages', 'quotes', 'appointments', 'leads']) {
      if (Array.isArray(inner[key])) return inner[key] as T[];
    }
  }

  // Check all known top-level keys
  for (const key of ['data', 'items', 'records', 'results', 'list', 'messages', 'quotes', 'appointments', 'leads']) {
    if (Array.isArray(raw[key])) return raw[key] as T[];
  }

  return [];
}

// ===== Contact Messages =====
export interface ContactMessageApi {
  id: number;
  full_name: string;
  phone: string;
  email: string;
  service_type: string;
  property_address?: string;
  preferred_date?: string;
  message?: string;
  status: 'New' | 'Contacted' | 'Resolved';
  created_at: string;
}

export async function fetchMessages(): Promise<ContactMessageApi[]> {
  const raw = await apiGetRaw('/messages/list.php');
  return extractArrayRaw<ContactMessageApi>(raw);
}

// ===== Message Status Update =====
export interface MessageStatusPayload {
  id: number;
  status: 'New' | 'Contacted' | 'Resolved';
}

export async function updateMessageStatus(
  payload: MessageStatusPayload,
): Promise<ApiResponse<{ id?: number }>> {
  return apiPost<{ id?: number }>('/messages/update-status.php', payload);
}

// ===== Message Archive/Delete =====
export async function archiveMessage(
  id: number,
): Promise<ApiResponse<{ id?: number }>> {
  return apiPost<{ id?: number }>('/messages/delete.php', { id });
}

// ===== Quote Requests =====
export interface QuoteRequestApi {
  id: number;
  full_name: string;
  phone: string;
  email: string;
  property_address?: string;
  property_type: string;
  bedrooms?: string;
  bathrooms?: string;
  kitchens?: string;
  living_rooms?: string;
  dining_rooms?: string;
  offices?: string;
  garage?: string;
  square_feet?: string;
  cleaning_frequency?: string;
  cleaning_type?: string;
  extras?: string[];
  extras_json?: string;
  preferred_date?: string;
  preferred_time?: string;
  budget_range?: string;
  notes?: string;
  source?: string;
  status: 'new' | 'reviewing' | 'quoted' | 'contacted' | 'won' | 'lost' | 'archived';
  created_at: string;
}

export async function fetchQuotes(): Promise<QuoteRequestApi[]> {
  const raw = await apiGetRaw('/quotes/list.php');
  return extractArrayRaw<QuoteRequestApi>(raw);
}

// ===== Quote Detail =====
export async function fetchQuoteById(id: number): Promise<QuoteRequestApi | null> {
  const res = await apiGet<QuoteRequestApi>(`/quotes/view.php?id=${id}`);
  if (!res.success || !res.data) return null;
  return res.data;
}

// ===== Quote Status Update =====
export interface QuoteStatusPayload {
  id: number;
  status: 'new' | 'reviewing' | 'quoted' | 'contacted' | 'won' | 'lost' | 'archived';
}

export async function updateQuoteStatus(
  payload: QuoteStatusPayload,
): Promise<ApiResponse<{ id?: number }>> {
  return apiPost<{ id?: number }>('/quotes/update-status.php', payload);
}

// ===== Quote Archive/Delete =====
export async function archiveQuote(
  id: number,
): Promise<ApiResponse<{ id?: number }>> {
  return apiPost<{ id?: number }>('/quotes/delete.php', { id });
}

// ===== Appointments =====
export interface AppointmentLeadApi {
  id: number;
  full_name: string;
  phone: string;
  email: string;
  appointment_type: string;
  service_type: string;
  property_address?: string;
  preferred_date?: string;
  preferred_time?: string;
  notes?: string;
  status: 'new' | 'contacted' | 'scheduled' | 'completed' | 'cancelled' | 'archived';
  source?: string;
  created_at: string;
}

export async function fetchAppointments(): Promise<AppointmentLeadApi[]> {
  const raw = await apiGetRaw('/appointments/list.php');
  const all = extractArrayRaw<AppointmentLeadApi>(raw);
  // Exclude ai_assistant leads — those belong to the AI Leads tab
  return all.filter((item) => item.source !== 'ai_assistant');
}

export async function fetchAiLeads(): Promise<AppointmentLeadApi[]> {
  // Try filtered endpoint first; fall back to full list + client-side filter
  const raw = await apiGetRaw('/appointments/list.php?source=ai_assistant');
  const filtered = extractArrayRaw<AppointmentLeadApi>(raw);
  // If backend supports the filter, we get only ai_assistant records.
  // If it ignores the param and returns all, filter client-side.
  if (filtered.length > 0 && filtered.every((item) => item.source === 'ai_assistant')) {
    return filtered;
  }
  // Backend returned mixed or empty — fetch all and filter
  const rawAll = await apiGetRaw('/appointments/list.php');
  const all = extractArrayRaw<AppointmentLeadApi>(rawAll);
  return all.filter((item) => item.source === 'ai_assistant');
}

// ===== Appointment Detail =====
export async function fetchAppointmentById(id: number): Promise<AppointmentLeadApi | null> {
  const res = await apiGet<AppointmentLeadApi>(`/appointments/view.php?id=${id}`);
  if (!res.success || !res.data) return null;
  return res.data;
}

// ===== Appointment Status Update =====
export interface AppointmentStatusPayload {
  id: number;
  status: 'new' | 'contacted' | 'scheduled' | 'completed' | 'cancelled' | 'archived';
}

export async function updateAppointmentStatus(
  payload: AppointmentStatusPayload,
): Promise<ApiResponse<{ id?: number }>> {
  return apiPost<{ id?: number }>('/appointments/update-status.php', payload);
}

// ===== Appointment Archive/Delete =====
export async function archiveAppointment(
  id: number,
): Promise<ApiResponse<{ id?: number }>> {
  return apiPost<{ id?: number }>('/appointments/delete.php', { id });
}

// ===== Admin Hero CRUD =====
export async function adminFetchHeroSlides(): Promise<{ data: HeroSlideApi[]; error: string | null }> {
  try {
    const raw = await apiGetRaw('/hero/list.php');
    // Check for hard error markers set by apiGetRaw
    if (raw['_httpStatus'] || raw['_error']) {
      const msg = String(raw['_error'] || `HTTP ${raw['_httpStatus']}`);
      return { data: [], error: msg };
    }
    // Use the same robust extractor — also probe 'slides' key specifically for this endpoint
    const probeKeys = ['slides', 'data', 'items', 'records', 'results', 'list'];
    // Direct array?
    if (Array.isArray(raw)) return { data: raw as HeroSlideApi[], error: null };
    // Check .data first
    const d = raw['data'];
    if (Array.isArray(d)) return { data: d as HeroSlideApi[], error: null };
    if (d && typeof d === 'object') {
      const inner = d as Record<string, unknown>;
      for (const key of probeKeys) {
        if (Array.isArray(inner[key])) return { data: inner[key] as HeroSlideApi[], error: null };
      }
    }
    for (const key of probeKeys) {
      if (Array.isArray(raw[key])) return { data: raw[key] as HeroSlideApi[], error: null };
    }
    return { data: [], error: null };
  } catch (err) {
    return { data: [], error: err instanceof Error ? err.message : 'Network error' };
  }
}

export interface HeroSavePayload {
  id?: number;
  tag: string;
  title: string;
  subtitle: string;
  cta_primary: string;
  cta_secondary: string;
  sort_order: number;
  active: number;
}

export async function adminSaveHero(payload: HeroSavePayload): Promise<ApiResponse<HeroSlideApi>> {
  return apiPost<HeroSlideApi>('/hero/save.php', payload);
}

export async function adminDeleteHero(id: number): Promise<ApiResponse<{ id?: number }>> {
  return apiPost<{ id?: number }>('/hero/delete.php', { id });
}

export async function adminUploadHeroImage(formData: FormData): Promise<ApiResponse<{ url?: string }>> {
  return apiUpload<{ url?: string }>('/hero/upload.php', formData);
}

// ===== Admin Services CRUD =====
export async function adminFetchServices(): Promise<{ data: ServiceApi[]; error: string | null }> {
  try {
    const raw = await apiGetRaw('/services/list.php');
    if (raw['_httpStatus'] || raw['_error']) {
      return { data: [], error: String(raw['_error'] || `HTTP ${raw['_httpStatus']}`) };
    }
    const probeKeys = ['services', 'data', 'items', 'records', 'results', 'list'];
    if (Array.isArray(raw)) return { data: raw as ServiceApi[], error: null };
    const d = raw['data'];
    if (Array.isArray(d)) return { data: d as ServiceApi[], error: null };
    if (d && typeof d === 'object') {
      const inner = d as Record<string, unknown>;
      for (const key of probeKeys) {
        if (Array.isArray(inner[key])) return { data: inner[key] as ServiceApi[], error: null };
      }
    }
    for (const key of probeKeys) {
      if (Array.isArray(raw[key])) return { data: raw[key] as ServiceApi[], error: null };
    }
    return { data: [], error: null };
  } catch (err) {
    return { data: [], error: err instanceof Error ? err.message : 'Network error' };
  }
}

export interface ServiceSavePayload {
  id?: number;
  title: string;
  short_description: string;
  full_description: string;
  icon: string;
  image_url?: string;
  sort_order: number;
  active: number;
}

export async function adminSaveService(payload: ServiceSavePayload): Promise<ApiResponse<ServiceApi>> {
  return apiPost<ServiceApi>('/services/save.php', payload);
}

export async function adminDeleteService(id: number): Promise<ApiResponse<{ id?: number }>> {
  return apiPost<{ id?: number }>('/services/delete.php', { id });
}

export async function adminUploadServiceImage(formData: FormData): Promise<ApiResponse<{ url?: string }>> {
  return apiUpload<{ url?: string }>('/services/upload.php', formData);
}

// ===== Admin Service Areas CRUD =====
export async function adminFetchServiceAreas(): Promise<{ data: ServiceAreaApi[]; error: string | null }> {
  try {
    const raw = await apiGetRaw('/service-areas/list.php');
    if (raw['_httpStatus'] || raw['_error']) {
      return { data: [], error: String(raw['_error'] || `HTTP ${raw['_httpStatus']}`) };
    }
    const probeKeys = ['areas', 'data', 'items', 'records', 'results', 'list'];
    if (Array.isArray(raw)) return { data: raw as ServiceAreaApi[], error: null };
    const d = raw['data'];
    if (Array.isArray(d)) return { data: d as ServiceAreaApi[], error: null };
    if (d && typeof d === 'object') {
      const inner = d as Record<string, unknown>;
      for (const key of probeKeys) {
        if (Array.isArray(inner[key])) return { data: inner[key] as ServiceAreaApi[], error: null };
      }
    }
    for (const key of probeKeys) {
      if (Array.isArray(raw[key])) return { data: raw[key] as ServiceAreaApi[], error: null };
    }
    return { data: [], error: null };
  } catch (err) {
    return { data: [], error: err instanceof Error ? err.message : 'Network error' };
  }
}

export interface ServiceAreaSavePayload {
  id?: number;
  city_name: string;
  state: string;
  display_order: number;
  active: number;
}

export async function adminSaveServiceArea(payload: ServiceAreaSavePayload): Promise<ApiResponse<ServiceAreaApi>> {
  return apiPost<ServiceAreaApi>('/service-areas/save.php', payload);
}

export async function adminDeleteServiceArea(id: number): Promise<ApiResponse<{ id?: number }>> {
  return apiPost<{ id?: number }>('/service-areas/delete.php', { id });
}

// ===== Admin Gallery CRUD =====
export async function adminFetchGallery(): Promise<{ data: GalleryImageApi[]; error: string | null }> {
  try {
    const raw = await apiGetRaw('/gallery/list.php');
    if (raw['_httpStatus'] || raw['_error']) {
      return { data: [], error: String(raw['_error'] || `HTTP ${raw['_httpStatus']}`) };
    }
    const probeKeys = ['images', 'gallery', 'data', 'items', 'records', 'results', 'list'];
    if (Array.isArray(raw)) return { data: raw as GalleryImageApi[], error: null };
    const d = raw['data'];
    if (Array.isArray(d)) return { data: d as GalleryImageApi[], error: null };
    if (d && typeof d === 'object') {
      const inner = d as Record<string, unknown>;
      for (const key of probeKeys) {
        if (Array.isArray(inner[key])) return { data: inner[key] as GalleryImageApi[], error: null };
      }
    }
    for (const key of probeKeys) {
      if (Array.isArray(raw[key])) return { data: raw[key] as GalleryImageApi[], error: null };
    }
    return { data: [], error: null };
  } catch (err) {
    return { data: [], error: err instanceof Error ? err.message : 'Network error' };
  }
}

export interface GallerySavePayload {
  id?: number;
  image_url?: string;
  title: string;
  alt_text: string;
  category: string;
  sort_order: number;
  active: number;
}

export async function adminSaveGallery(payload: GallerySavePayload): Promise<ApiResponse<GalleryImageApi>> {
  return apiPost<GalleryImageApi>('/gallery/save.php', payload);
}

export async function adminDeleteGallery(id: number): Promise<ApiResponse<{ id?: number }>> {
  return apiPost<{ id?: number }>('/gallery/delete.php', { id });
}

export async function adminUploadGalleryImage(formData: FormData): Promise<ApiResponse<{ url?: string }>> {
  return apiUpload<{ url?: string }>('/gallery/upload.php', formData);
}

// ===== Admin Content CRUD =====
export interface ContentSavePayload {
  lang: string;
  content_key: string;
  content_value: string;
}

export interface ContentSaveBatchPayload {
  lang: string;
  items: Array<{ content_key: string; content_value: string }>;
}

export async function adminFetchContent(lang: string): Promise<ContentItem[]> {
  // Probe extractor — tries all known shapes
  const extractItems = (raw: Record<string, unknown>): ContentItem[] | null => {
    if (Array.isArray(raw)) return raw as ContentItem[];
    const d = raw['data'];
    if (Array.isArray(d)) return d as ContentItem[];
    if (d && typeof d === 'object') {
      const inner = d as Record<string, unknown>;
      for (const k of ['items', 'records', 'list', 'content']) {
        if (Array.isArray(inner[k])) return inner[k] as ContentItem[];
      }
    }
    for (const k of ['items', 'records', 'list', 'content', 'data']) {
      if (Array.isArray(raw[k])) return raw[k] as ContentItem[];
    }
    return null;
  };

  // Try primary endpoint: /content/get.php
  try {
    const raw = await apiGetRaw(`/content/get.php?lang=${encodeURIComponent(lang)}`);
    if (!raw['_httpStatus'] && !raw['_error']) {
      const found = extractItems(raw);
      if (found && found.length > 0) return found;
    }
  } catch { /* fall through */ }

  // Fallback endpoint: /content/list.php
  try {
    const raw2 = await apiGetRaw(`/content/list.php?lang=${encodeURIComponent(lang)}`);
    if (!raw2['_httpStatus'] && !raw2['_error']) {
      const found2 = extractItems(raw2);
      if (found2 && found2.length > 0) return found2;
    }
  } catch { /* fall through */ }

  return [];
}

export async function adminSaveContent(payload: ContentSavePayload): Promise<ApiResponse<ContentItem>> {
  return apiPost<ContentItem>('/content/save.php', payload);
}

export async function adminSaveContentBatch(payload: ContentSaveBatchPayload): Promise<ApiResponse<{ saved?: number }>> {
  return apiPost<{ saved?: number }>('/content/save.php', payload);
}

// ===== Admin Facebook CRUD =====
export interface FacebookSavePayload {
  facebook_url: string;
  embed_code: string;
  show_feed?: number;
}

export async function adminFetchFacebook(): Promise<FacebookSettings | null> {
  try {
    const raw = await apiGetRaw('/facebook/get.php');
    if (raw['_httpStatus'] || raw['_error']) return null;
    // Probe { settings: {} }, { data: { settings: {} } }, { data: {} } (flat)
    const tryParse = (obj: Record<string, unknown>): FacebookSettings | null => {
      const s = obj['settings'] as Record<string, unknown> | undefined;
      if (s && typeof s === 'object') {
        return {
          facebook_url: String(s['facebook_url'] || ''),
          embed_code: String(s['embed_code'] || ''),
          show_feed: (s['show_feed'] as number | boolean) ?? 1,
        };
      }
      // flat object with facebook_url directly
      if (obj['facebook_url'] !== undefined || obj['embed_code'] !== undefined) {
        return {
          facebook_url: String(obj['facebook_url'] || ''),
          embed_code: String(obj['embed_code'] || ''),
          show_feed: (obj['show_feed'] as number | boolean) ?? 1,
        };
      }
      return null;
    };
    const d = raw['data'];
    if (d && typeof d === 'object') {
      const result = tryParse(d as Record<string, unknown>);
      if (result) return result;
    }
    return tryParse(raw);
  } catch {
    return null;
  }
}

export async function adminSaveFacebook(payload: FacebookSavePayload): Promise<ApiResponse<FacebookSettings>> {
  return apiPost<FacebookSettings>('/facebook/save.php', payload);
}

// ===== Admin Change Password =====
export interface ChangePasswordPayload {
  current_password: string;
  new_password: string;
}

export async function adminChangePassword(
  payload: ChangePasswordPayload,
): Promise<ApiResponse<{ message?: string }>> {
  try {
    const res = await fetch('https://completehomecarecleaning.com/api/admin/change-password.php', {
      method: 'POST',
      headers: getAdminHeaders(),
      credentials: 'include',
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      if (res.status === 401) {
        // Distinguish between "wrong current password" and "session invalid"
        try {
          const errorJson = (await res.json()) as { error?: string; message?: string };
          const errMsg = errorJson.error || errorJson.message || '';
          const isPasswordError = errMsg.toLowerCase().includes('current password') ||
                                  errMsg.toLowerCase().includes('password');
          if (isPasswordError) {
            return { success: false, message: errMsg };
          }
        } catch {
          // If body can't be parsed, fall through to redirect
        }
        if (typeof window !== 'undefined' && window.REACT_APP_NAVIGATE) {
          window.REACT_APP_NAVIGATE('/admin/login');
        }
        return { success: false, message: 'Unauthorized — Please login again' };
      }
      return { success: false, message: `HTTP ${res.status}` };
    }
    const json = (await res.json()) as ApiResponse<{ message?: string }> & { error?: string };
    if (!json.success) {
      return { success: false, message: json.error || json.message || 'Unknown error' };
    }
    return json;
  } catch (err) {
    return {
      success: false,
      message: err instanceof Error ? err.message : 'Network error',
    };
  }
}